import React, { FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useFetch } from 'use-http';
import { push, replace } from 'connected-react-router';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import IndexReferenceType from 'src/models/IndexReferenceType';
// import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import ContractHoldDocs from '../LeasingComponents/redesign/ContractHoldDocs';
import actions from 'src/actions';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import DialogList from 'src/dialogs';
import AddWIOwnershipData from 'src/models/tract_detail/add_ownership_revised/AddWIOwnership';
import ContractWorkingInterestList from '../LeasingComponents/redesign/ContractWorkingInterestList';
import PropertyUtil from 'src/util/PropertyUtil';
import Owner from 'src/models/tract_detail/Owner';
import WIOwnership from 'src/models/tract_detail/WIOwnership';
import Ownership from 'src/models/tract_detail/Ownership';
import WIContractOwnership from 'src/models/tract_detail/WIContractOwnership';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
// import MathUtil from 'src/util/MathUtil';
import BrineLease, { BrineLeaseHold } from 'src/models/contract_types/BrineLease';
import ProvisionClauseLeaseholdXRef from 'src/models/ProvisionClauseLeaseholdXRef';
import LandContractType from 'src/models/LandContractType';
import AgreementUtil from 'src/util/AgreementUtil';
import ProvisionClause from 'src/components/common/ProvisionClause';
import ExtensionOption from 'src/models/ExtensionOption';
import ExtensionOptionList from '../LeasingComponents/ExtensionOptionList';
import { IRootState } from 'src/reducers';
import AddOwnershipPages from './AddOwnershipPages';

const BrineLeaseInfo = () => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { register, handleSubmit, reset, errors, formState, getValues, setValue, watch } = useForm();
    const { get, put, post, response, del } = useFetch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    const dispatch = useDispatch();

    // REDUCERS
    const { project } = useSelector((state: IRootState) => state);

    const [brineLeaseData, setBrineLeaseData] = useState<BrineLease>(undefined);
    const [contractLookupId, setContractLookupId] = useState<number>(undefined);
    const [provisionLeaseClauses, setProvisionLeaseClauses] = useState<Array<IndexReferenceType>>(undefined);
    const [measurementTypes, setMeasurementTypes] = useState<Array<IndexReferenceType>>(undefined);
    const [attachedClauses, setAttachedClauses] = useState<Array<ProvisionClauseLeaseholdXRef>>(undefined);
    const [currentLeaseHold, setCurrentLeaseHold] = useState(0);
    const [selectedWIOwnershipId, setSelectedWIOwnershipId] = useState(0);
    const [royalty, setRoyalty] = useState(0);
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [extensionOptions, setExtensionOptions] = useState<Array<ExtensionOption>>([]);
    const [totalNetAcres, setTotalNetAcres] = useState<number>(0);
    const [copyLeaseholdClicked, setCopyLeaseholdClicked] = useState<boolean>(false);
    const [sourceSelected, setSourceSelected] = useState<boolean>(false);
    const [isReadonly, setIsReadonly] = useState<boolean>(false);

    const { isDirty } = formState;

    useEffect(() => {
        getMeasurementTypes();
        getProvisionClauses();
        if (appContext.brineLeaseId > 0) {
            getBrineLeaseData(appContext.brineLeaseId);
        } else {
            setIsReadonly(false);
        }
    }, []);

    useEffect(() => {
        setDataLoaded(!!measurementTypes && !!brineLeaseData && !!provisionLeaseClauses && !!attachedClauses);
    }, [measurementTypes, brineLeaseData, provisionLeaseClauses, attachedClauses]);

    useEffect(() => {
        if (dataLoaded === true) {
            reset(prepData(brineLeaseData));
        }
    }, [dataLoaded, currentLeaseHold]);

    useEffect(() => {
        if (brineLeaseData?.id > 0) {
            if (appContext.brineLeaseHoldId > 0 && brineLeaseData?.brineLeaseHolds?.findIndex((slh) => slh.id === appContext.brineLeaseHoldId) > -1) {
                const leaseHoldIndex = brineLeaseData?.brineLeaseHolds?.findIndex((slh) => slh.id === appContext.brineLeaseHoldId);
                setCurrentLeaseHold(leaseHoldIndex);
                setContractLookupId(brineLeaseData.brineLeaseHolds[leaseHoldIndex].contractLookupId);
                setRoyalty(brineLeaseData?.brineLeaseHolds?.[leaseHoldIndex]?.royalty);
                getAttachedClauses(brineLeaseData.brineLeaseHolds[leaseHoldIndex].contractLookupId);
            } else if (appContext.brineLeaseHoldId === 0) {
                const tempBrineLeaseData = brineLeaseData;
                tempBrineLeaseData.brineLeaseHolds.push(new BrineLeaseHold());
                setBrineLeaseData(tempBrineLeaseData);
                setCurrentLeaseHold(tempBrineLeaseData?.brineLeaseHolds?.length - 1);
                setRoyalty(0);
                setAttachedClauses([]);
            }
        }
    }, [brineLeaseData]);

    // Disable Copy Leasehold button until Source Leasehold is selected
    useEffect(() => {
        setSourceSelected(false);
    }, [copyLeaseholdClicked]);

    const initializePage = () => {
        setDataLoaded(false);
        setBrineLeaseData(undefined);
        setAttachedClauses(undefined);
    };

    // Enable Copy Leasehold button when the selected Source changes from default
    const handleSourceSelected = (e: FormEvent<HTMLSelectElement>) => {
        const selectedItem = e.target as HTMLSelectElement;

        if (selectedItem.value !== '-- Select --') {
            setSourceSelected(true);
            setCopyLeaseholdClicked(true);
        }
    };

    const prepData = useCallback(
        (
            roughLeaseData: BrineLease
        ) => {
            const formData: BrineLease = {
                ...roughLeaseData,
                reminderDate: roughLeaseData.reminderDate ? (moment(roughLeaseData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null,
                createdDate: roughLeaseData.createdDate ? (moment(roughLeaseData.createdDate).format('YYYY-MM-DD') as unknown as Date) : null,
                leaseDate: roughLeaseData.leaseDate ? (moment(roughLeaseData.leaseDate).format('YYYY-MM-DD') as unknown as Date)
                    : roughLeaseData.createdDate ? (moment(roughLeaseData.createdDate).format('YYYY-MM-DD') as unknown as Date) : null,
                effectiveDate: roughLeaseData.effectiveDate ? (moment(roughLeaseData.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null,
                commitDate: roughLeaseData.commitDate ? (moment(roughLeaseData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null,
                inHandDate: roughLeaseData.inHandDate ? (moment(roughLeaseData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null,
                voidDate: roughLeaseData.voidDate ? (moment(roughLeaseData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null,
                recordedDate: roughLeaseData.recordedDate ? (moment(roughLeaseData.recordedDate).format('YYYY-MM-DD') as unknown as Date) : null,

                currentHold: {
                    ...roughLeaseData.brineLeaseHolds?.[currentLeaseHold],
                }
            };

            if (formData.currentHold.extensionOptions?.length > 0) {
                setExtensionOptions(formData.currentHold.extensionOptions);
            }

            if (formData.isGeneratedLease === true && formData.isInHand === true) {
                setIsReadonly(true);
            } else {
                setIsReadonly(false);
            }

            if (provisionLeaseClauses?.length > 0) {
                formData.currentHold.provisionLeaseClauses = [];
                for (let clause of provisionLeaseClauses) {
                    let aClause = attachedClauses?.find(ac => ac.provisionLeaseClauseId === clause.id);
                    let pClause: ProvisionClauseLeaseholdXRef = {
                        id: aClause ? aClause.id : 0,
                        provisionLeaseClauseId: clause.id,
                        contractLookupId: aClause ? aClause.contractLookupId : formData.currentHold.contractLookupId,
                        description: aClause ? aClause.description : '',
                        isSelected: aClause ? aClause.isSelected : null
                    };
                    formData.currentHold.provisionLeaseClauses.push(pClause);
                }
            }

            setTotalNetAcres(formData.originalGrossAcres);

            return formData;
        },
        [currentLeaseHold, attachedClauses, measurementTypes, provisionLeaseClauses]
    );

    const getBrineLeaseData = async (brineLeaseId: number) => {
        const result: BrineLease = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/brineLease/${brineLeaseId}`
        );
        if (response.ok) {
            console.log('Brine Leasehold data, ', result);
            setBrineLeaseData(result);
        } else {
            window.alert('Get Brine Lease Data failed');
        }
    };

    const getAttachedClauses = async (lookupId: number) => {
        const result: ProvisionClauseLeaseholdXRef[] = await get(
            `/v1.0/provisionLeaseClause/contracts/${lookupId}`
        );

        if (response.ok) {
            setAttachedClauses(result);
        } else {
            window.alert('Could not get attached Lease Clauses!');
        }
    };

    const getProvisionClauses = async () => {
        const result: IndexReferenceType[] = await get(
            `/v1.0/provisionLeaseClause/contract-type/${LandContractType.BrineLease}`
        );

        if (response.ok) {
            setProvisionLeaseClauses(result);
        } else {
            window.alert('Could not get Provision Lease Clauses!');
        }
    };

    const getMeasurementTypes = async () => {
        const result = await get(`/v1.0/referenceTypes`);

        if (response.ok) {
            setMeasurementTypes(result);
        }
    };

    const updateBrineLease = async (data: BrineLease) => {
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 3, 3);

        let result = await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/brineLease`,
            data
        );

        if (response.ok) {
            initializePage();
            getBrineLeaseData(appContext.brineLeaseId);
            if (appContext.brineLeaseHoldId > 0) {
                dispatch(
                    push(window.location.pathname)
                );

                getAttachedClauses(contractLookupId);
            } else {
                dispatch(
                    replace(
                        `${currentPath.join('/')}/${result.contractId}/brine-lease-holds/${result.contractHoldId}`
                    )
                );
            }
        } else {
            window.alert('Update Brine Lease failed');
        }
    };

    const deleteBrineLeasehold = () => {
        const switchToLeaseholdId = brineLeaseData?.brineLeaseHolds.find((x) => x.id !== brineLeaseData?.brineLeaseHolds?.[currentLeaseHold].id).id;
        const deleteId = brineLeaseData?.brineLeaseHolds?.[currentLeaseHold].id;
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 1, 1);

        const confirmAction = async () => {
            // tslint:disable-next-line: max-line-length
            await del(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/brineLease/${deleteId}/false`);
            if (response.ok) {
                getBrineLeaseData(appContext.brineLeaseId);
                dispatch(
                    replace(
                        `${currentPath.join('/')}/${switchToLeaseholdId}`
                    )
                );
            } else {
                window.alert('delete leasehold failed');
            }
        };

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>Are you sure you want to delete this Leasehold?</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Leasehold`, body, confirmAction);
    };

    const deleteBrineLease = () => {
        const deleteId = brineLeaseData?.brineLeaseHolds?.[currentLeaseHold].id;
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 6, 6);

        const confirmAction = async () => {
            // tslint:disable-next-line: max-line-length
            await del(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/brineLease/${deleteId}/true`);
            if (response.ok) {
                dispatch(
                    replace(
                        `${currentPath.join('/')}`
                    )
                );
            } else {
                window.alert('delete leasehold failed');
            }
        };

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>This is the only leasehold. The lease will also be deleted. Continue?</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Leasehold`, body, confirmAction);
    };

    const createBrineLease = async (data: BrineLease) => {
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 3, 3);

        let result = await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/brineLease`,
            data
        );
        if (response.ok) {
            initializePage();
            dispatch(
                replace(
                    `${currentPath.join('/')}/${result.contractId}/brine-lease-holds/${result.contractHoldId}`
                )
            );
            getBrineLeaseData(result.contractId);
        } else {
            window.alert('Create Brine Lease failed');
        }
    };

    const addLeaseholdClauses = async (data: Array<ProvisionClauseLeaseholdXRef>) => {
        await post(
            `/v1.0/provisionLeaseClause`,
            data
        );

        if (response.ok) {
            getAttachedClauses(contractLookupId);
        }
    };

    const updateLeaseholdClauses = async (data: Array<ProvisionClauseLeaseholdXRef>) => {
        await put(
            `/v1.0/provisionLeaseClause`,
            data
        );

        if (response.ok) {
            getAttachedClauses(contractLookupId);
        }
    };

    const deleteLeaseholdClauses = async (deleteClauseIds: Array<number>) => {
        let query = '';
        for (let id of deleteClauseIds) {
            if (query.length > 0) {
                query += `&deleteClauseIds=${id}`;
            } else {
                query += `?deleteClauseIds=${id}`;
            }
        }

        await del(
            `/v1.0/provisionLeaseClause/contracts/${brineLeaseData.brineLeaseHolds[currentLeaseHold].contractLookupId}${query}`,
            deleteClauseIds
        );

        if (response.ok) {
            getAttachedClauses(contractLookupId);
        }
    };

    const onSubmit = (data: BrineLease) => {
        data.brineLeaseHolds = new Array<BrineLeaseHold>(data.currentHold);
        data = finalizeBrineLease(data);
        // data.id = leaseData.id;
        handleLeaseholdClauses(data.currentHold.provisionLeaseClauses);
        if (data.id > 0) {
            updateBrineLease(data);
        } else {
            data.isInHand = true;
            data.inHandDate = new Date();
            data.isCommitted = true;
            data.commitDate = new Date();
            createBrineLease(data);
        }
    };

    const handleLeaseholdClauses = (provisionClauses: Array<ProvisionClauseLeaseholdXRef>) => {
        let deleteClauseIds: number[] = [];
        let addClauses: ProvisionClauseLeaseholdXRef[] = [];
        let updateClauses: ProvisionClauseLeaseholdXRef[] = [];

        for (let clause of provisionClauses) {
            if (clause.id > 0 && clause.isSelected === null) {
                deleteClauseIds.push(clause.provisionLeaseClauseId);
            } else if (clause.id === 0 && clause.isSelected !== null) {
                addClauses.push(clause);
            } else if (clause.id > 0) {
                updateClauses.push(clause);
            }
        }

        if (deleteClauseIds.length > 0) {
            deleteLeaseholdClauses(deleteClauseIds);
        }
        if (addClauses.length > 0) {
            addLeaseholdClauses(addClauses);
        }
        if (updateClauses.length > 0) {
            updateLeaseholdClauses(updateClauses);
        }
    };

    const finalizeBrineLease = (data: BrineLease) => {
        data.id = brineLeaseData?.id ? parseInt(brineLeaseData?.id + '', 10) : 0;
        data.leaseNumber = brineLeaseData?.leaseNumber ? brineLeaseData?.leaseNumber : 0;
        data.rteLegalDescription = brineLeaseData?.rteLegalDescription ? brineLeaseData.rteLegalDescription : '';

        data.clientDocNumber = data.clientDocNumber ? data.clientDocNumber : '';
        data.leasedBy = data.leasedBy ? data.leasedBy : '';
        data.lesseeName = data.lesseeName ? data.lesseeName : '';
        data.leaseLessorNotes = data.leaseLessorNotes ? data.leaseLessorNotes : '';
        data.legalDescription = data.legalDescription ? data.legalDescription : '';
        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';
        data.originalLessor = data.originalLessor ? data.originalLessor : '';
        data.lessorAka = data.lessorAka ? data.lessorAka : '';
        data.originalLessee = data.originalLessee ? data.originalLessee : '';

        data.reminderDate = data.reminderDate
            ? (moment(data.reminderDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.createdDate = data.createdDate
            ? (moment(data.createdDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.effectiveDate = data.effectiveDate
            ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.commitDate = data.commitDate
            ? (moment(data.createdDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.inHandDate = data.inHandDate
            ? (moment(data.inHandDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.leaseDate = data.leaseDate
            ? (moment(data.leaseDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.recordedDate = data.recordedDate
            ? (moment(data.recordedDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        data.createdByUserId = data.createdByUserId ? parseInt(data.createdByUserId + '', 10) : null;
        data.bonusTypeId = data.bonusTypeId ? parseInt(data.bonusTypeId + '', 10) : null;

        data.bonusAmount = data.bonusAmount ? parseFloat(data.bonusAmount + '') : null;
        data.originalGrossAcres = data.originalGrossAcres ? parseFloat(data.originalGrossAcres + '') : null;

        data.isCommitted = data.isCommitted ? data.isCommitted : null;
        data.isInHand = data.isInHand ? data.isInHand : null;
        data.isFiledByMemo = !!data.isFiledByMemo ? true : null;

        if (!!brineLeaseData) {
            data.clientDocNumber = brineLeaseData?.clientDocNumber ? brineLeaseData.clientDocNumber : '';
            data.reminderNote = brineLeaseData?.reminderNote ? brineLeaseData.reminderNote : '';
            data.lesseeAddressLine1 = brineLeaseData?.lesseeAddressLine1 ? brineLeaseData.lesseeAddressLine1 : '';
            data.lesseeAddressLine2 = brineLeaseData?.lesseeAddressLine2 ? brineLeaseData.lesseeAddressLine2 : '';
            data.lesseeCity = brineLeaseData?.lesseeCity ? brineLeaseData.lesseeCity : '';
            data.lesseeState = brineLeaseData?.lesseeState ? brineLeaseData.lesseeState : '';
            data.lesseeZipCode = brineLeaseData?.lesseeZipCode ? brineLeaseData.lesseeZipCode : '';
            data.ownersAddressOnDocument = brineLeaseData?.ownersAddressOnDocument ? brineLeaseData.ownersAddressOnDocument : '';
            data.ownersSSNTaxIdOnDocument = brineLeaseData?.ownersSSNTaxIdOnDocument ? brineLeaseData.ownersSSNTaxIdOnDocument : '';
            data.ownersOnDocument = brineLeaseData.ownersOnDocument ? brineLeaseData.ownersOnDocument : '';

            data.isNotOriginalLessee = brineLeaseData?.isNotOriginalLessee ? brineLeaseData.isNotOriginalLessee : null;
            data.isGeneratedLease = brineLeaseData?.isGeneratedLease ? brineLeaseData.isGeneratedLease : false;
            data.isVoid = brineLeaseData?.isVoid ? brineLeaseData.isVoid : false;
            data.isInHand = brineLeaseData?.isInHand ? brineLeaseData.isInHand : false;
            data.isCommitted = brineLeaseData?.isCommitted ? brineLeaseData.isCommitted : false;

            data.voidDate = brineLeaseData?.voidDate ? (moment(brineLeaseData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.commitDate = brineLeaseData?.commitDate ? (moment(brineLeaseData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.inHandDate = brineLeaseData?.inHandDate ? (moment(brineLeaseData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.reminderDate = brineLeaseData?.reminderDate ? (moment(brineLeaseData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null;
        }

        if (data.brineLeaseHolds?.length > 0) {
            let formatted = new Array<BrineLeaseHold>();
            data.brineLeaseHolds.forEach((brineLeaseHold: BrineLeaseHold) => {
                if (formatted.length === currentLeaseHold && !!data.currentHold) {
                    formatted.push(finalizeBrineLeaseHold(data.currentHold));
                } else {
                    formatted.push(finalizeBrineLeaseHold(brineLeaseHold));
                }
            });
            data.brineLeaseHolds = [...formatted];
            if (data.currentHold.provisionLeaseClauses?.length > 0) {
                data.currentHold.provisionLeaseClauses.forEach((pClause: ProvisionClauseLeaseholdXRef) => {
                    pClause.id = pClause.id ? parseInt(pClause.id + '', 10) : 0;
                    pClause.contractLookupId = pClause.contractLookupId ? parseInt(pClause.contractLookupId + '', 10) : 0;
                    pClause.provisionLeaseClauseId = pClause.provisionLeaseClauseId ? parseInt(pClause.provisionLeaseClauseId + '', 10) : 0;
                    pClause.isSelected = pClause.isSelected ? AgreementUtil.parseBool(pClause.isSelected + '') : null;
                });
            }
        }

        return data;
    };

    const finalizeBrineLeaseHold = (data: BrineLeaseHold) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = project.currentProject?.id;
        data.brineLeaseId = brineLeaseData?.id ? brineLeaseData.id : 0;

        data.number = data.number ? parseInt(data.number + '', 10) : null;
        data.primaryRentalTerm = data.primaryRentalTerm ? parseInt(data.primaryRentalTerm + '', 10) : null;
        data.primaryUnitTypeId = data.primaryUnitTypeId ? parseInt(data.primaryUnitTypeId + '', 10) : null;
        data.primaryRentalFeeTypeId = data.primaryRentalFeeTypeId ? parseInt(data.primaryRentalFeeTypeId + '', 10) : null;
        data.extensionOptionLimit = data.extensionOptionLimit ? parseInt(data.extensionOptionLimit + '', 10) : null;

        data.primaryRentalAmount = data.primaryRentalAmount ? parseFloat(data.primaryRentalAmount + '') : null;
        data.primaryRentalTotal = data.primaryRentalTotal ? parseFloat(data.primaryRentalTotal + '') : null;
        data.royalty = data.royalty ? parseFloat(data.royalty + '') : null;

        data.isActive = data.isActive ? data.isActive : null;
        data.isHistorical = data.isHistorical ? data.isHistorical : null;

        if (data.contractOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.contractOwnerships.forEach((owner: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(owner));
            });
            data.contractOwnerships = [...formatted];
        }

        if (data.extensionOptions?.length > 0) {
            for (let extension of data.extensionOptions) {
                extension.id = extension.id ? parseInt(extension.id + '', 10) : 0;
                extension.optionTerm = extension.optionTerm ? parseInt(extension.optionTerm + '', 10) : null;
                extension.optionUnitTypeId = extension.optionUnitTypeId ? parseInt(extension.optionUnitTypeId + '', 10) : null;
                extension.optionFeeTypeId = extension.optionFeeTypeId ? parseInt(extension.optionFeeTypeId + '', 10) : null;
                extension.optionAmount = extension.optionTotal ? parseFloat(extension.optionAmount + '') : null;
                extension.optionTotal = extension.optionTotal ? parseFloat(extension.optionTotal + '') : null;
            }
        }

        return data;
    };

    const finalizeWIOwnership = (data: WIContractOwnership) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = data.projectId ? parseInt(data.id + '', 10) : 0;
        data.ownerLookUpId = data.ownerLookUpId ? parseInt(data.ownerLookUpId + '', 10) : 0;
        data.parentOwnershipId = data.parentOwnershipId ? parseInt(data.parentOwnershipId + '', 10) : 0;
        data.ownershipId = data.ownershipId ? parseInt(data.ownershipId + '', 10) : 0;
        data.contractLookupId = data.contractLookupId ? parseInt(data.contractLookupId + '', 10) : 0;

        data.decimalInterest = data.decimalInterest ? parseFloat(data.decimalInterest + '') : 0;

        data.note = data.note ? data.note : '';

        if (data.orriOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.orriOwnerships.forEach((orri: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(orri));
            });
            data.orriOwnerships = [...formatted];
        }

        return data;
    };

    const getOptions = (type: string, item: string = '') => {
        if (measurementTypes?.length > 0) {
            return measurementTypes
                .filter((mt) => mt.description === type)
                .map((option) => {
                    if (type === 'Fee' && option.name === 'Per Item') {
                        return { value: option.id, label: `Per ${item}` };
                    }
                    return { value: option.id, label: option.name };
                });
        }
        return [];
    };

    const previousContractHold = () => {
        if (currentLeaseHold > 0) {
            const brineLeaseHoldId = brineLeaseData?.brineLeaseHolds?.[currentLeaseHold - 1]?.id;
            setCurrentLeaseHold(currentLeaseHold - 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/brine-leases/${appContext.brineLeaseId}/brine-lease-holds/${brineLeaseHoldId}`
                )
            );
        }
    };

    const nextContractHold = () => {
        if (currentLeaseHold + 1 < brineLeaseData?.brineLeaseHolds?.length) {
            const brineLeaseHoldId = brineLeaseData?.brineLeaseHolds?.[currentLeaseHold + 1]?.id;

            setCurrentLeaseHold(currentLeaseHold + 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/brine-leases/${appContext.brineLeaseId}/brine-lease-holds/${brineLeaseHoldId}`
                )
            );
        }
    };

    // const calculateTotal = (
    //     measurementTypeId: number,
    //     paymentAmount: number,
    //     grossAcres: number,
    //     measurementTypesList: Array<IndexReferenceType>
    // ) => {
    //     let measurementType = measurementTypesList.find((mt) => mt.id === measurementTypeId)?.name;

    //     return measurementType !== undefined ? Number(MathUtil.cleanDollarAmount(measurementType === 'Per Item' ? paymentAmount * grossAcres : paymentAmount)) : 0;
    // };

    const onSaveAddOwnership = async (values: AddWIOwnershipData) => {
        values.contractOwnership.contractLookupId = brineLeaseData.brineLeaseHolds[currentLeaseHold].contractLookupId;

        await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/brineLease/contract-ownership`,
            values
        );

        if (response.ok) {
            getBrineLeaseData(appContext.brineLeaseId);
        } else {
            window.alert('Update Contract Ownership failed');
        }
    };

    const onTryAddWI = () => {
        if (!isNaN(brineLeaseData?.brineLeaseHolds?.[currentLeaseHold]?.id)) {
            dialogContext.contract.addContractOwnership(
                DialogList.ADD_WI_OWNERSHIP,
                'Add Working Interest Ownership',
                undefined,
                undefined,
                appContext.clientNumber,
                appContext.projectNumber,
                AddOwnershipPages.AddOwnershipLandingPage,
                (path: string) => dispatch(push(path)),
                null,
                onSaveAddOwnership
            );
        } else {
            dispatch(actions.layout.showDialog(DialogList.LEASEHOLD_SAVE_BEFORE_ADDING_WI));
        }
    };

    const onAddORRI = (newParentOwnershipId: number) => {
        dialogContext.contract.addContractOwnership(
            DialogList.ADD_ORRI_OWNERSHIP,
            'Add Overriding Royalty Interest Ownership',
            undefined,
            newParentOwnershipId,
            appContext.clientNumber,
            appContext.projectNumber,
            AddOwnershipPages.AddOwnershipLandingPage,
            (path: string) => dispatch(push(path)),
            null,
            onSaveAddOwnership
        );
    };

    const onCopyORRI = (ownershipId: number) => {
        dialogContext.leasehold.cloneORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), () => location.reload());
    };

    const ownerNameForWorkingInterest = (workingInterestId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => brineLeaseData?.brineLeaseHolds?.[currentLeaseHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => brineLeaseData?.brineLeaseHolds?.[currentLeaseHold]?.contractOwnerships);
        const ownership = wiOwnerships?.filter((wio: Ownership) => wio.id === workingInterestId)[0];
        const owner = wiOwners.filter((o: Owner) => o.ownerLookUpId === ownership.ownerLookUpId)[0];

        return owner?.name || '[Error]';
    };

    const onDeleteORRI = (ownershipId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => brineLeaseData?.brineLeaseHolds?.[currentLeaseHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => brineLeaseData?.brineLeaseHolds?.[currentLeaseHold]?.contractOwnerships);
        const ownership: WIOwnership = wiOwnerships?.filter((wio: WIOwnership) => wio.id === ownershipId)[0];

        dialogContext.leasehold.deleteORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), ownership.orriOwnerships, wiOwners, () =>
            location.reload()
        );
    };

    const onDeleteContractOwnership = async (contractOwnershipId: number) => {
        const result: BrineLease = await del(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/brineLease/contract-ownership/${contractOwnershipId}`
        );
        if (response.ok) {
            console.log('Successfully Delete Contract Ownership ', result);
            dispatch(actions.layout.hideDialog());
            getBrineLeaseData(appContext?.brineLeaseId);
        } else {
            window.alert('Get Surface Waiver Data failed');
        }
    };

    const onUpdateWINote = async (ownership: Ownership) => {
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/surfaceTracts/notes`,
            ownership
        );

        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getBrineLeaseData(appContext.brineLeaseId);
        } else {
            window.alert(`update wi note failed. note: ${ownership.note}`);
        }
    };

    const findOwnership = (ownershipId: number = 0) => {
        let refId = ownershipId > 0 ? ownershipId : selectedWIOwnershipId;
        let ownership = brineLeaseData.brineLeaseHolds[currentLeaseHold].contractOwnerships?.find(
            (lo: WIContractOwnership) => lo.id === refId
        );

        if (!ownership) {
            brineLeaseData.brineLeaseHolds[currentLeaseHold].contractOwnerships?.forEach((lo: WIContractOwnership) => {
                let tempOwnership = lo.orriOwnerships.find((wio: WIContractOwnership) => wio.id === refId);
                if (tempOwnership) {
                    ownership = tempOwnership;
                }
            });
        }

        return ownership;
    };

    const openOwnershipNotes = (ownershipId: number) => {
        // this.setState({ ownershipNotesId: ownershipId });
        setSelectedWIOwnershipId(ownershipId);
        dialogContext.tract.openOwnershipNotes(
            findOwnership(ownershipId),
            brineLeaseData?.brineLeaseHolds?.[currentLeaseHold]?.wiOwners,
            '',
            2,
            (ownership: Ownership) => onUpdateWINote(ownership)
        );
    };

    const displayClauses = () => {
        let clauseRows: Array<Array<IndexReferenceType>> = [[]];
        let rowIndex = 0;

        for (let i = 0; i < provisionLeaseClauses.length; i++) {
            if (clauseRows[rowIndex].length < 8) {
                clauseRows[rowIndex].push(provisionLeaseClauses[i]);
            } else {
                clauseRows.push([]);
                rowIndex++;
            }
        }

        return clauseRows.map((cr, index) => {
            return (
                <div className="form-row" key={`provisionClause-${index + 1}`}>
                    {cr.map((pc: IndexReferenceType, rIndex: number) => {
                        let currentIndex = (index * 8) + (rIndex);
                        return (
                            <ProvisionClause
                                label={pc.name}
                                formPrefix={`currentHold.provisionLeaseClauses[${currentIndex}]`}
                                descriptionRequired={true}
                                register={register}
                                errors={errors}
                                key={`provisionClause-${index}.${rIndex + 1}`}
                            />
                        );
                    })}
                </div>
            );
        });
    };

    const onAddOption = () => {
        let ref = getValues();
        let optionLimit = ref.currentHold.extensionOptionLimit;

        if (isNaN(Number(optionLimit))) {
            window.alert('Option limit must have a value!');
        } else if (optionLimit <= extensionOptions.length) {
            window.alert('Maximum extension options reached!');
        } else {
            let optionList = [...extensionOptions];
            let newOption = new ExtensionOption();
            optionList.push(newOption);
            setExtensionOptions(optionList);
        }
    };

    const onCopyLeasehold = async () => {
        setAttachedClauses(undefined);
        setDataLoaded(false);
        let copyLeaseholdId = getValues('sourceLeaseholdId');

        // tslint:disable-next-line: max-line-length
        const result = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/brineLease/brine-lease-hold/${appContext.brineLeaseHoldId}/copy-leasehold/${copyLeaseholdId}`);

        if (response.ok) {
            let currentPath = window.location.pathname.split('/');
            currentPath.splice(currentPath.length - 1, 1);

            dispatch(
                push(`${currentPath.join('/')}/${result}`)
            );
            getBrineLeaseData(appContext.brineLeaseId);
        } else {
            window.alert('call failed');
        }
    };

    const leaseholdList = () => {
        if (brineLeaseData?.brineLeaseHolds?.length > 1) {
            let holds = [...brineLeaseData.brineLeaseHolds];
            holds.pop();
            let leaseholdIds = holds.map((lh: BrineLeaseHold) => {
                return { value: lh.id || 0, label: lh.number?.toString() || 'Error' };
            });

            return leaseholdIds;
        } else {
            return [];
        }
    };

    const warningStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '0.5rem',
        margin: '0 0 1rem 0',
        fontWeight: 700,
        background: '#d5d5d5',
        fontSize: '.8em',
        color: '#565656',
        width: '100%',
        textAlign: 'center',
        borderRadius: '0.4rem',
    };

    return (
        <div className="lease-info-page">
            <div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div>
                        {isDirty && (
                            <>
                                <div
                                    id="dirtyFlag"
                                    style={{
                                        position: 'fixed',
                                        top: '4.7rem',
                                        left: '0',
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(255,0,0,.5)',
                                        zIndex: 9000,
                                        padding: '1rem 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                    <div style={{ position: 'absolute', right: '.5rem', top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
                                        <div>
                                            <Button text="Save" type="submit" size="small" intent="primary" />
                                        </div>
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button text="Cancel Changes" size="small" onClick={() => reset()} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Added to let the user know fields with * are required */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }}>
                        <span style={{ color: 'red', transform: 'scale(2)' }}>*</span>
                        <p>= Fields are required!</p>
                    </div>

                    <RHFLegend title="Brine Lease" />
                    <div hidden={true}>
                        <RHFInputBox label="" name="id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFCheckbox name="isGeneratedLease" label="" disabled={true} register={register} errors={errors} />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="Brine Lease Number"
                            name="leaseNumber"
                            style={{ width: '100%' }}
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                        <RHFInputBox
                            label="Lease Date"
                            name="leaseDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Effective Date"
                            name="effectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                            disabled={isReadonly}
                        />
                        <RHFInputBox label="Volume" name="volume" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Page" name="page" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Inst. Number" name="instrumentNumber" register={register} type="text" errors={errors} />
                        <RHFInputBox
                            label="Recorded Date"
                            name="recordedDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                        <RHFToggle label="Filed by Memorandum" name="isFiledByMemo" register={register} errors={errors} disabled={isReadonly} />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="Original Gross Acres"
                            name="originalGrossAcres"
                            register={register}
                            type="number"
                            errors={errors}
                            step={0.0001}
                            isRequired={true}
                            disabled={isReadonly}
                        />

                        <RHFInputBox
                            label="Original Lessor"
                            name="originalLessor"
                            register={register}
                            type="text"
                            errors={errors}
                            isRequired={true}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Original Lessee"
                            name="originalLessee"
                            register={register}
                            type="text"
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFInputBox label="Lessor AKA" name="lessorAka" register={register} type="text" errors={errors} disabled={isReadonly} />
                    </div>
                    {/* TODO: Set this up for multiple Brine Leaseholds. */}
                    <RHFLegend title="Brine Leasehold" />
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        <div className="form-row">
                            <Button text="Previous" onClick={previousContractHold} size="small" intent="primary" />
                            {/* <span onClick={previousLeasehold} style={{ border: '1px solid black' }} className="clickable">Previous</span> */}
                            <span style={{ padding: '.5rem' }}>{`${currentLeaseHold + 1} of ${brineLeaseData?.brineLeaseHolds?.length || 1}`}</span>
                            <Button text="Next" onClick={nextContractHold} size="small" intent="primary" />
                            {/* <span onClick={nextLeasehold} style={{ border: '1px solid black' }} className="clickable">Next</span> */}
                        </div>
                        {brineLeaseData?.brineLeaseHolds?.length > 1 && (
                            <div style={{ display: 'flex' }}>
                                {copyLeaseholdClicked && (
                                    <div style={{ textAlign: 'center', marginRight: '1rem' }}>
                                        <RHFSelect
                                            onChange={(e) => handleSourceSelected(e)}
                                            name="sourceLeaseholdId"
                                            label="Source Leasehold"
                                            options={leaseholdList()}
                                            register={register}
                                            disabled={isReadonly}
                                        />
                                        <Button
                                            style={{ marginTop: '1rem' }}
                                            disabled={!sourceSelected || isReadonly}
                                            text="Confirm Copy"
                                            intent="danger"
                                            onClick={onCopyLeasehold}
                                        />
                                    </div>
                                )}
                                <div>
                                    {appContext.brineLeaseHoldId === 0 && (
                                        <Button
                                            style={{ marginLeft: '2rem' }}
                                            text="Copy Leasehold"
                                            onClick={() => setCopyLeaseholdClicked(true)}
                                            intent="primary"
                                        />
                                    )}
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteBrineLeasehold()}
                                        intent="danger"
                                    />
                                </div>
                            </div>
                        )}
                        {brineLeaseData?.brineLeaseHolds?.length === 1 && (
                            <div style={{ display: 'flex' }}>
                                <div>
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteBrineLease()}
                                        intent="danger"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="form-row">
                        <RHFCheckbox
                            style={{ marginBottom: '2rem' }}
                            label={'Active Lease'}
                            name={'currentHold.isActive'}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Extension Option Limit"
                            name="currentHold.extensionOptionLimit"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={isReadonly}
                        />
                    </div>
                    <div hidden={true}>
                        <RHFInputBox label="" name="currentHold.id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.brineLeaseId" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.projectId" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.number" register={register} disabled={true} errors={errors} />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Primary Rental Term"
                            name="currentHold.primaryRentalTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFSelect
                            label=" "
                            name="currentHold.primaryUnitTypeId"
                            options={getOptions('Time')}
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Rental Amount"
                            name="currentHold.primaryRentalAmount"
                            register={register}
                            type="number"
                            errors={errors}
                            step={0.0001}
                            disabled={isReadonly}
                        />
                        <RHFSelect
                            label=" "
                            name="currentHold.primaryRentalFeeTypeId"
                            options={getOptions('Fee', 'acre')}
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Rental Total"
                            name="currentHold.primaryRentalTotal"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={isReadonly}
                            step={0.0001}
                        />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="Royalty"
                            name="currentHold.royalty"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={isReadonly}
                            step={0.0001}
                        />
                    </div>

                    {extensionOptions?.length > 0 && (
                        <ExtensionOptionList
                            extensionOptions={extensionOptions}
                            measurementTypes={measurementTypes}
                            namePrefix="currentHold.extensionOptions"
                            netAcres={totalNetAcres}
                            calculateTotals={false}
                            // calculateTotal={calculateTotal}
                            getValues={getValues}
                            setValue={setValue}
                            register={register}
                            errors={errors}
                            isReadonly={isReadonly}
                        />
                    )}

                    <div className="form-row">
                        <Button text="Add Option" onClick={onAddOption} size="small" intent="primary" />
                    </div>

                    <RHFLegend title="Original Legal Description" />
                    <div className="form-row">
                        <RHFTextArea
                            label={''}
                            name="legalDescription"
                            style={{
                                width: '100%',
                                height: '6rem',
                                border: '0',
                                borderBottom: '1px solid black',
                                resize: 'none',
                                overflowY: 'auto',
                            }}
                            register={register}
                            disabled={isReadonly}
                        />
                    </div>

                    <RHFLegend title="Provisional Clauses" />
                    {provisionLeaseClauses && (
                        displayClauses()
                    )}

                    <RHFLegend title="Document Management" />
                    {brineLeaseData?.brineLeaseHolds && (
                        <ContractHoldDocs
                            showDialog={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            hideDialog={() => dispatch(actions.layout.hideDialog())}
                            contractLookupId={brineLeaseData?.brineLeaseHolds?.[currentLeaseHold]?.contractLookupId}
                            contractType={'Contract Hold'}
                        />
                    )}

                    <div style={{ minHeight: '20rem', marginTop: '2rem' }}>
                        <RHFLegend title="Contract Ownership" />
                        <span style={warningStyle}>ALL INTEREST REPRESENTED AS DECIMAL</span>
                        <ContractWorkingInterestList
                            isGovStateBlm={false}
                            owners={brineLeaseData?.brineLeaseHolds?.[currentLeaseHold]?.wiOwners}
                            wiContractOwnerships={brineLeaseData?.brineLeaseHolds?.[currentLeaseHold]?.contractOwnerships}
                            prefix={'currentHold'}
                            setDialogShowing={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            tryAddWI={onTryAddWI}
                            onAddORRI={onAddORRI}
                            onCopyORRI={onCopyORRI}
                            onDeleteORRI={onDeleteORRI}
                            deleteContractOwnership={onDeleteContractOwnership}
                            openOwnershipNotes={openOwnershipNotes}
                            register={register}
                            errors={errors}
                            getValues={getValues}
                            setValue={setValue}
                            watch={watch}
                            royalty={royalty}
                            contractType="brineLease"
                        />
                    </div>

                    <div style={{ marginTop: '5rem' }}>
                        <Button text="Save" type="submit" intent="primary" />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BrineLeaseInfo;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/BrineLeaseInfo.tsx